import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup, Placeholder } from 'semantic-ui-react';
import { RateDetailHook, RateMetadataHook } from '../../../../hooks/rate';
import {
  billingCycleOptions,
  BillingService,
  CommonUtility,
  ToastMessage,
} from '../../../../utility';
import {
  populateRate,
  prepareRateCategories,
  RateCardFields,
} from '../../../../page-components';
import {
  CheckboxFormField,
  ControlledTextAreaFormField,
  ControlledTextFormField,
  DropdownFormField,
  ShadowBox,
} from '../../../../elements';
import { AlertFooterBar } from '../../../../elements/FooterAlert';

const WarehouseSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  description: yup.string().optional(),
  isActive: yup.boolean(),
  billingCycleDays: yup
    .array()
    .min(1, 'Billing Cycle is required')
    .required('Billing Cycle is required'),
});

export function RateDetails() {
  const { pathname } = useLocation();
  const [userValue, setUserValue] = useState({});
  const [processing, setProcessing] = useState(false);

  const rateId = useMemo(() => {
    if (location.pathname) {
      return CommonUtility.getSegmentByPosition(location.pathname, 4);
    }
    return null;
  }, [pathname]);

  const {
    formState: { errors, dirtyFields },
    control,
    reset,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(WarehouseSchema),
  });

  const [changed, setChanged] = useState(false);

  const { data, setData, loading } = RateDetailHook(rateId);
  const { data: metadata } = RateMetadataHook();

  useEffect(() => {
    if (CommonUtility.isValidObject(data) && CommonUtility.isValidObject(metadata)) {
      resetFields();
    }
  }, [data, metadata]);

  const populateUserValue = () => {
    const value = populateRate(metadata, data);
    setUserValue(value);
  };

  const onValueChange = (categoryName, serviceName, keyName, value) => {
    setChanged(true);
    setUserValue(userValue => ({
      ...userValue,
      [categoryName]: {
        ...userValue[categoryName],
        [serviceName]: {
          ...userValue[categoryName][serviceName],
          [keyName]: value,
        },
      },
    }));
  };

  const resetFields = () => {
    reset({
      name: data.name,
      description: data.description,
      isActive: data.isActive,
      billingCycleDays: data.billingCycleDays,
    });
    populateUserValue();
    setChanged(false);
  };

  const update = async formValues => {
    try {
      setProcessing(true);
      const payload = {
        name: formValues.name,
        description: formValues.description,
        isActive: formValues.isActive,
        warehouseId: data?.warehouseId?._id || data?.warehouseId,
        billingCycleDays: formValues.billingCycleDays,
        categories: prepareRateCategories(userValue, metadata),
        id: data._id,
      };

      const res = await BillingService.update(data._id, payload);
      setData(res);
      ToastMessage.success('Service updated successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading || processing) {
    return (
      <div className="mt-3">
        <ShadowBox>
          <Placeholder fluid>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
          </Placeholder>
        </ShadowBox>
        <ShadowBox className="mt-4">
          <Placeholder fluid>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
            <Placeholder.Header>
              <Placeholder.Line />
              <Placeholder.Line />
              <Placeholder.Line />
            </Placeholder.Header>
          </Placeholder>
        </ShadowBox>
      </div>
    );
  }

  return (
    <div>
      <ShadowBox>
        <h3>Information</h3>
        <Form>
          <FormGroup widths="equal">
            <ControlledTextFormField
              control={control}
              name="name"
              errors={errors.name}
              hint={errors?.name?.message}
              label="Name"
            />
            <ControlledTextAreaFormField
              control={control}
              name="description"
              errors={errors.description}
              hint={errors?.description?.message}
              label="Description"
              rows={1}
            />
            <CheckboxFormField
              control={control}
              name="isActive"
              errors={errors.isActive}
              hint={errors?.isActive?.message}
              label="Active"
            />
          </FormGroup>
          <b>Scheduling</b>
          <FormGroup className="mt-4 mb-0">
            <DropdownFormField
              control={control}
              name="billingCycleDays"
              errors={errors.billingCycleDays}
              hint={errors?.billingCycleDays?.message}
              label="Billing Cycle Day"
              options={billingCycleOptions}
              multiple
              width={4}
              sorted={false}
            />
          </FormGroup>
        </Form>
      </ShadowBox>
      <RateCardFields
        metadata={metadata}
        userValue={userValue}
        onValueChange={onValueChange}
      />
      {(CommonUtility.isValidObject(dirtyFields) || changed) && (
        <AlertFooterBar
          message="Save Changes"
          cancel={resetFields}
          save={handleSubmit(update)}
        />
      )}
    </div>
  );
}
